// Import child components
import './edit-dialog';

// Import dependencies from LitElement library
import { unsafeCSS, css, html, LitElement, render } from 'lit';
import { property } from 'lit/decorators.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import recipe builder services
import { getRecipeSetting } from '@automator/builder/services';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Mixin that provides common publish settings functionality for an AutomatorBuilder web component.
 *
 * @param {LitElement} superClass - The base class to extend from.
 * @return {LitElement} The class extended with the mixin functionality.
 */
const AutomatorBuilderPublishSettingMixin = ( superClass ) => class extends superClass {
	// Define styles using unsafeCSS and external SCSS file
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Unique identifier for the component.
	 * @type {String}
	 */
	@property( { type: String, attribute: 'setting-id' } ) accessor settingID;

	/**
	 * Icon ID to display.
	 * @type {String}
	 * @default 'cog'
	 */
	@property( { type: String } ) accessor icon = 'cog';

	/**
	 * Label text for the setting.
	 * @type {String}
	 */
	@property( { type: String } ) accessor label;

	/**
	 * Fields object containing data for the setting.
	 * @type {Object}
	 */
	@property( { type: Object } ) accessor fields = {};

	/**
	 * Fields object containing the attributes of the fields
	 * @type {Array}
	 */
	@property( { type: Array } ) accessor fieldsDirectory = [];

	/**
	 * The helper text for the setting.
	 * @type {String}
	 */
	@property( { type: String } ) accessor helper;

	/**
	 * Lifecycle method called when the component is first updated.
	 * Sets the fields property to the value returned by the `getRecipeSetting` method.
	 * Sets fields only once when `settingID` becomes available.
	 */
	firstUpdated() {
		this.fields = getRecipeSetting( this.settingID );
	}

	/**
	 * Renders the HTML structure for the component.
	 * @return {TemplateResult|undefined} The template to be rendered.
	 */
	render() {
		// Check if we should show the setting at all
		if ( ! this.shouldRender ) {
			return;
		}

		return html`
			<div class="setting">
				${ this._templateIcon }

				<div class="setting-content">
					${ this._templateLabel }
					${ this._templateReadableValue }
					${ this._templateHelper }
					${ this._templateEditButton }
				</div>
			</div>
		`;
	}

	/**
	 * Renders the icon template.
	 * @return {TemplateResult} The icon template.
	 */
	get _templateIcon() {
		return html`
			<div class="setting-icon">
				<uo-icon id="${ this.icon }"></uo-icon>
			</div>
		`;
	}

	/**
	 * Renders the label template.
	 * @return {TemplateResult} The label template.
	 */
	get _templateLabel() {
		return html`
			<div class="setting-label">
				${ this.label }:
			</div>
		`;
	}

	/**
	 * Renders the readable value of the fields in JSON format.
	 * @return {TemplateResult} The readable value template.
	 */
	get _templateReadableValue() {
		return html`
			<div class="setting-value">
				${ this.readableValue }
			</div>
		`;
	}

	/**
	 * Renders the helper template if the `helper` property is set.
	 * @return {TemplateResult|undefined} The helper template or undefined if `helper` is not set.
	 */
	get _templateHelper() {
		if ( isEmpty( this.helper ) ) {
			return;
		}

		return html`
			<div class="setting-helper">
				<uo-tooltip>
					${ this.helper }

					<uo-button
						slot="target"
						@click=${ this.helperCallback }
						size="extra-small"
						color="transparent"
					>
						<uo-icon id="question-circle"></uo-icon>
					</uo-button>
				</uo-tooltip>
			</div>
		`;
	}

	/**
	 * Renders the "Edit" button template if the fields object is not empty.
	 * @return {TemplateResult|undefined} The edit button template or undefined if `fields` is empty.
	 */
	get _templateEditButton() {
		if ( isEmpty( this.fieldsDirectory ) ) {
			return;
		}

		return html`
			<div class="setting-edit-button">
				<uo-button
					@click=${ this.editButtonCallback }
					size="extra-small"
					color="transparent"
				>
					${ escapeHTML( __( 'Edit', 'uncanny-automator' ) ) }
				</uo-button>
			</div>
		`;
	}

	/**
	 * Creates and renders the edit fields dialog for the publish settings.
	 */
	createEditFieldsDialog() {
		// Create a wrapper div to host the dialog, appended to the body to avoid z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		// Render the dialog into the wrapper
		render(
			html`
				<uap-builder-publish-settings-edit-dialog
					setting-id=${ this.settingID }

					.fields=${ this.fields }
					.fieldsDirectory=${ this.fieldsDirectory }
					@uap-builder-components-set-recipe-setting=${ fields => this.fields = fields }
				></uap-builder-publish-settings-edit-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Callback for the helper button click event.
	 * This method should be overridden by the child class.
	 */
	helperCallback() {
		// To be defined by the child class
	}

	/**
	 * Callback for the "Edit" button click event.
	 * Opens the edit fields dialog.
	 */
	editButtonCallback() {
		this.createEditFieldsDialog();
	}

	/**
	 * Gets the value of a field by its code.
	 *
	 * @param {String} fieldCode - The code of the field to get the value for.
	 * @return {Mixed|undefined} The value of the field or undefined if the field does not exist.
	 */
	getFieldValue( fieldCode ) {
		if ( this.fields?.[ fieldCode ] ) {
			return this.fields[ fieldCode ].value;
		}
	}

	/**
	 * Gets the readable value of the fields as a JSON string.
	 *
	 * @return {String} The fields object serialized to JSON format.
	 */
	get readableValue() {
		return JSON.stringify( this.fields );
	}

	/**
	 * Gets the visibility status of the setting.
	 *
	 * @return {Boolean} The visibility status
	 */
	get shouldRender() {
		return true;
	}
}

// Export the AutomatorBuilderPublishSetting class by applying the mixin to LitElement
export const AutomatorBuilderPublishSetting = AutomatorBuilderPublishSettingMixin( LitElement );
